import * as THREE from 'three';

/** Material parameters shared by every opaque surface definition. */
export type SurfaceMaterialParams = THREE.MeshStandardMaterialParameters;

export interface SurfaceMaterialDefaults {
  roughness: number;
  metalness?: number;
  /** True when the custom shader writes an absolute albedo. */
  shaderOwnsAlbedo?: boolean;
  flatShading?: boolean;
}

export function clampSurface01(value: number, fallback = 0): number {
  return Math.min(Math.max(Number.isFinite(value) ? value : fallback, 0), 1);
}

/**
 * Create the common MeshStandardMaterial shell used by custom surface
 * definitions. The shader can then own color/roughness/normal without a
 * profile tint being multiplied in a second time.
 */
export function createSurfaceMaterial(
  params: SurfaceMaterialParams,
  defaults: SurfaceMaterialDefaults,
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    ...params,
    roughness: defaults.roughness,
    flatShading: defaults.flatShading ?? params.flatShading ?? true,
    side: params.side ?? THREE.FrontSide,
  });
  applySurfaceMaterialDefaults(material, defaults);
  return material;
}

export function applySurfaceMaterialDefaults(
  material: THREE.MeshStandardMaterial,
  defaults: SurfaceMaterialDefaults,
): void {
  material.roughness = clampSurface01(defaults.roughness, material.roughness);
  if (defaults.shaderOwnsAlbedo) material.color.set('#ffffff');
  if (defaults.metalness !== undefined) {
    material.metalness = clampSurface01(defaults.metalness, material.metalness);
  }
}

/** Copy the standard shell properties that Material.clone() does not preserve for custom shaders. */
export function copySurfaceMaterialProperties(
  source: THREE.MeshStandardMaterial,
  target: THREE.MeshStandardMaterial,
): void {
  target.blending = source.blending;
  target.depthTest = source.depthTest;
  target.depthWrite = source.depthWrite;
  target.colorWrite = source.colorWrite;
  target.premultipliedAlpha = source.premultipliedAlpha;
  target.polygonOffset = source.polygonOffset;
  target.polygonOffsetFactor = source.polygonOffsetFactor;
  target.polygonOffsetUnits = source.polygonOffsetUnits;
  target.toneMapped = source.toneMapped;
  target.fog = source.fog;
}

export function surfaceProgramCacheKey(
  id: string,
  version: number,
  ...parts: Array<string | number | boolean>
): string {
  return [id, ...parts, `v${version}`].join('-');
}
